import { useMemo, type ComponentProps } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Loader2 } from 'lucide-react';
import type { AdTemplate } from '@shared/schema';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { selectInspirationCards } from './inspiration-cards';
import './canvas-inspiration-drawer.css';

interface CanvasInspirationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUseTemplate: (style: string) => void;
  onCloseAutoFocus?: ComponentProps<typeof SheetContent>['onCloseAutoFocus'];
}

/** Shares Create an ad's sampler; selecting a card only fills the brief. */
export function CanvasInspirationDrawer({ open, onOpenChange, onUseTemplate, onCloseAutoFocus }: CanvasInspirationDrawerProps) {
  const { data: templates = [], isPending, isError, isFetching, refetch } = useQuery<AdTemplate[]>({
    queryKey: ['/api/ad-templates'],
    enabled: open,
  });
  const samples = useMemo(() => selectInspirationCards(templates), [templates]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="canvas-inspiration-drawer" data-testid="canvas-inspiration-drawer" onCloseAutoFocus={onCloseAutoFocus}>
        <SheetHeader className="canvas-inspiration-drawer__header">
          <SheetTitle className="canvas-inspiration-drawer__title">Inspiration</SheetTitle>
          <SheetDescription className="canvas-inspiration-drawer__description">
            Choose a template to fill your brief. Edit it, then generate.
          </SheetDescription>
        </SheetHeader>
        <div className="canvas-inspiration-drawer__body" aria-busy={isFetching}>
          {isPending && (
            <div className="canvas-inspiration-drawer__state" role="status">
              <Loader2 size={20} className="animate-spin" aria-hidden="true" />
              <p>Loading inspiration…</p>
            </div>
          )}
          {isError && (
            <div className="canvas-inspiration-drawer__state" role="alert">
              <p>Inspiration couldn’t load. Try again.</p>
              <button type="button" className="canvas-inspiration-drawer__retry" onClick={() => { void refetch(); }} disabled={isFetching}>
                {isFetching ? 'Retrying…' : 'Try again'}
              </button>
            </div>
          )}
          {!isPending && !isError && !samples.length && (
            <div className="canvas-inspiration-drawer__state" role="status">
              <p>No inspiration templates are available yet.</p>
            </div>
          )}
          {!!samples.length && (
            <div className="canvas-inspiration-drawer__grid">
              {samples.map(({ style, template }) => (
                <button key={template.id} type="button" className="canvas-inspiration-drawer__card" data-testid={`inspiration-card-${template.id}`}
                  aria-label={`Use ${style} template`} onClick={() => onUseTemplate(style)}>
                  <img src={template.imageUrl} alt={template.name} loading="lazy" />
                  <span className="canvas-inspiration-drawer__caption">
                    {style} <ArrowRight size={15} aria-hidden="true" />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
